import { useCallback, useEffect, useState } from 'react';
import { type FilterState } from '@vello/analytics-semantic';
import { type AnalyticsBiFilterSet } from '@vello/api-client';
import { type BiErrorView, BI_SAVE_POLICY, biErrorView, filterSetErrorView, grantsAllow } from '../../../src/index';
import { useAdmin } from '../../session';
import { type Loadable } from './bi-hooks';

/**
 * The operator's saved filter sets (D528): a name and a filter state kept on the SERVER, per operator, shareable with the team (read-only for
 * everybody else). Like the saved reports, nothing is cached beyond the component's state: a change is a server call and the list is what the
 * server answered.
 */
export type SetOutcome<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: BiErrorView };

export interface FilterSetsApi {
  readonly state: Loadable<readonly AnalyticsBiFilterSet[]>;
  /** The operator holds `analytics:save_report`: the screen offers to keep, change and delete sets. */
  readonly canSave: boolean;
  readonly reload: () => void;
  readonly add: (input: { name: string; state: FilterState; shared: boolean }) => Promise<SetOutcome<AnalyticsBiFilterSet>>;
  readonly update: (id: string, patch: { name?: string; state?: FilterState; shared?: boolean }) => Promise<SetOutcome<AnalyticsBiFilterSet>>;
  readonly remove: (id: string) => Promise<SetOutcome<undefined>>;
}

export function useFilterSets(): FilterSetsApi {
  const { runtime, grants } = useAdmin();
  const canSave = grants !== undefined && grantsAllow(grants, BI_SAVE_POLICY);
  const [state, setState] = useState<Loadable<readonly AnalyticsBiFilterSet[]>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const api = runtime.analyticsBi;

  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    void api.filterSets().then((res) => {
      if (!live) return;
      setState(res.ok ? { status: 'ready', value: res.value.sets } : { status: 'error', error: biErrorView(res.error) });
    });
    return () => {
      live = false;
    };
  }, [api, attempt]);

  const upsert = useCallback((set: AnalyticsBiFilterSet): void => {
    setState((s) => (s.status === 'ready' ? { status: 'ready', value: [set, ...s.value.filter((x) => x.id !== set.id)] } : s));
  }, []);

  const add = useCallback<FilterSetsApi['add']>(
    async (input) => {
      const res = await api.saveFilterSet(input);
      if (!res.ok) return { ok: false, error: filterSetErrorView(res.error) };
      upsert(res.value.set);
      return { ok: true, value: res.value.set };
    },
    [api, upsert],
  );
  const update = useCallback<FilterSetsApi['update']>(
    async (id, patch) => {
      const res = await api.updateFilterSet(id, patch);
      if (!res.ok) return { ok: false, error: filterSetErrorView(res.error) };
      upsert(res.value.set);
      return { ok: true, value: res.value.set };
    },
    [api, upsert],
  );
  const remove = useCallback<FilterSetsApi['remove']>(
    async (id) => {
      const res = await api.deleteFilterSet(id);
      // A set that is already gone (deleted in another tab) is gone: the list just drops it.
      if (!res.ok && res.error.reasonCode !== 'ANALYTICS_FILTER_SET_NOT_FOUND') return { ok: false, error: filterSetErrorView(res.error) };
      setState((s) => (s.status === 'ready' ? { status: 'ready', value: s.value.filter((x) => x.id !== id) } : s));
      return { ok: true, value: undefined };
    },
    [api],
  );

  return { state, canSave, reload: useCallback(() => setAttempt((n) => n + 1), []), add, update, remove };
}
